import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  RefreshCw,
  TrendingUp,
  UserPlus,
  Search,
  User,
  MapPin,
  Users,
  Sparkles,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ---------------------------------------------------------------------------
// Shared visual meta
// ---------------------------------------------------------------------------
const RELATIONSHIP_META = {
  existing: {
    key: 'existing',
    title: 'Existing Farmer',
    description: 'Customer already has a prior assessment. Choose whether to renew the sanctioned limit or request an enhancement.',
    icon: Users,
    color: '#2563eb',
    bg: '#eff6ff',
    border: '#bfdbfe',
  },
  new: {
    key: 'new',
    title: 'New Farmer',
    description: 'First-time assessment for this customer. Full profile, documents, and scoring pipeline will be collected.',
    icon: UserPlus,
    color: '#059669',
    bg: '#ecfdf5',
    border: '#a7f3d0',
  },
};

const EXISTING_SUB = {
  SAME_LIMIT_RENEWAL: { icon: RefreshCw, color: '#1d4ed8', bg: '#dbeafe', label: 'Same-Limit Renewal', time: '5–10 min' },
  ENHANCED_LIMIT: { icon: TrendingUp, color: '#7c3aed', bg: '#ede9fe', label: 'Enhanced Limit', time: '15–20 min' },
};

// ---------------------------------------------------------------------------
// Stepper
// ---------------------------------------------------------------------------
const Stepper = ({ step }) => {
  const steps = [
    { n: 1, label: 'Select Customer' },
    { n: 2, label: 'Choose Relationship' },
  ];
  return (
    <div className="flex items-center gap-3 mb-8" data-testid="start-assessment-stepper">
      {steps.map((s, i) => {
        const active = step === s.n;
        const done = step > s.n;
        return (
          <div key={s.n} className="flex items-center gap-2">
            <div
              className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${done ? 'bg-emerald-600 text-white' : active ? 'bg-slate-900 text-white' : 'bg-gray-200 text-gray-500'}`}
            >
              {done ? <Check className="h-3.5 w-3.5" /> : s.n}
            </div>
            <div className={`text-sm ${active ? 'font-semibold text-gray-900' : 'text-gray-500'}`}>{s.label}</div>
            {i < steps.length - 1 && <div className="w-10 h-px bg-gray-300 mx-2" />}
          </div>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Step 1 — Customer search + Add New
// ---------------------------------------------------------------------------
const CustomerStep = ({ farmers, loading, onSelect, onAddNew }) => {
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    if (!q.trim()) return farmers.slice(0, 25);
    const n = q.toLowerCase();
    return farmers
      .filter((f) => {
        const hay = [
          f.farmer_name,
          f.aadhaar_number,
          f.contact_number,
          f.residential_district,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return hay.includes(n);
      })
      .slice(0, 50);
  }, [q, farmers]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Search by name, Aadhaar, phone, or district"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9"
            data-testid="start-assessment-search-input"
          />
        </div>
        <Button
          onClick={onAddNew}
          className="shrink-0 bg-slate-900 hover:bg-slate-800 text-white"
          data-testid="start-assessment-add-new-btn"
        >
          <UserPlus className="h-4 w-4 mr-2" /> Add New Farmer
        </Button>
      </div>

      <div className="text-[11px] text-gray-500 -mt-2">
        Showing {filtered.length} of {farmers.length} customers. Narrow with the search box above.
      </div>

      <div className="border border-gray-200 rounded-md overflow-hidden bg-white">
        {loading ? (
          <div className="py-10 text-center text-sm text-gray-500">Loading customers…</div>
        ) : filtered.length === 0 ? (
          <div className="py-10 text-center text-sm text-gray-500">
            No customer matched &ldquo;{q}&rdquo;. Try a different search or add a new farmer.
          </div>
        ) : (
          <ul className="divide-y divide-gray-100 max-h-[480px] overflow-auto">
            {filtered.map((f) => (
              <li key={f.id}>
                <button
                  onClick={() => onSelect(f)}
                  className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors flex items-center gap-3"
                  data-testid={`start-assessment-customer-${f.id}`}
                >
                  <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                    <User className="h-4 w-4 text-slate-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 truncate">{f.farmer_name}</div>
                    <div className="text-[11px] text-gray-500 flex items-center gap-3 mt-0.5">
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" /> {f.residential_district || 'Unknown'}
                      </span>
                      {f.aadhaar_number && (
                        <span className="font-mono">····{String(f.aadhaar_number).slice(-4)}</span>
                      )}
                      {f.contact_number && <span>{f.contact_number}</span>}
                    </div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-gray-300" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Step 2 — Choose Relationship
// ---------------------------------------------------------------------------
const RelationshipStep = ({ farmer, previousAssessment, loadingPrev, onPick, onBack }) => {
  const hasPrev = Boolean(previousAssessment);
  const [mode, setMode] = useState(hasPrev ? 'existing' : null);
  useEffect(() => {
    setMode(hasPrev ? 'existing' : null);
  }, [hasPrev]);

  return (
    <div className="space-y-5">
      {/* Selected customer pill */}
      <div className="flex items-center justify-between bg-white border border-gray-200 rounded-md px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
            <User className="h-4 w-4 text-slate-500" />
          </div>
          <div>
            <div className="font-semibold text-gray-900">{farmer.farmer_name}</div>
            <div className="text-[11px] text-gray-500 flex items-center gap-3">
              <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {farmer.residential_district || 'Unknown'}</span>
              {farmer.aadhaar_number && <span className="font-mono">····{String(farmer.aadhaar_number).slice(-4)}</span>}
            </div>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="start-assessment-change-customer-btn">
          Change customer
        </Button>
      </div>

      {/* Auto-hint */}
      {!loadingPrev && (
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <Sparkles className="h-3.5 w-3.5 text-amber-500" />
          {hasPrev ? (
            <span>
              Prior assessment detected · Cycle <b>{previousAssessment.cycle_number}</b> · recommended path: <b>Existing Farmer</b>.
            </span>
          ) : (
            <span>No prior assessment on record for this customer · recommended path: <b>New Farmer</b>.</span>
          )}
        </div>
      )}

      {/* Relationship cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Object.values(RELATIONSHIP_META).map((r) => {
          const Icon = r.icon;
          const isRecommended = hasPrev ? r.key === 'existing' : r.key === 'new';
          const selected = mode === r.key;
          return (
            <Card
              key={r.key}
              className={`relative cursor-pointer transition-all ${selected ? 'ring-2 ring-offset-1' : 'hover:shadow-md'}`}
              style={{ borderColor: r.border, borderWidth: 1.5, ...(selected ? { boxShadow: `0 0 0 2px ${r.color}` } : {}) }}
              onClick={() => setMode(r.key)}
              data-testid={`relationship-card-${r.key}`}
            >
              {isRecommended && (
                <Badge className="absolute top-3 right-3 bg-amber-100 text-amber-800 border-none text-[10px]">Recommended</Badge>
              )}
              <CardHeader className="pb-3">
                <div
                  className="w-11 h-11 rounded-lg flex items-center justify-center mb-2"
                  style={{ background: r.bg }}
                >
                  <Icon className="h-5 w-5" style={{ color: r.color }} />
                </div>
                <CardTitle className="text-base">{r.title}</CardTitle>
                <CardDescription className="text-sm leading-relaxed">{r.description}</CardDescription>
              </CardHeader>
            </Card>
          );
        })}
      </div>

      {/* Sub-choice for existing */}
      {mode === 'existing' && (
        <div className="bg-white border border-gray-200 rounded-md p-4">
          <div className="text-sm font-semibold text-gray-900 mb-3">Select existing-customer journey</div>
          {!hasPrev && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-3">
              No prior assessment was found. You may still proceed by entering a manual prior cycle during the workflow, or switch to <b>New Farmer</b>.
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {Object.entries(EXISTING_SUB).map(([jt, meta]) => {
              const Icon = meta.icon;
              return (
                <button
                  key={jt}
                  className="text-left border border-gray-200 rounded-md p-3 hover:border-slate-400 hover:shadow-sm transition-all"
                  onClick={() => onPick(jt)}
                  data-testid={`existing-sub-${jt}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: meta.bg }}>
                      <Icon className="h-4 w-4" style={{ color: meta.color }} />
                    </div>
                    <Badge className="text-[10px] border-none" style={{ background: meta.bg, color: meta.color }}>
                      {meta.time}
                    </Badge>
                  </div>
                  <div className="mt-2 font-semibold text-gray-900 text-sm">{meta.label}</div>
                  <div className="text-[11px] text-gray-500 mt-0.5">
                    {jt === 'SAME_LIMIT_RENEWAL'
                      ? 'Renew the existing sanctioned limit with a lightweight refresh of CBS & externals.'
                      : 'Request a higher limit — adds cultivation update, cashflow, and cross-hold policy checks.'}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* CTA for new */}
      {mode === 'new' && (
        <div className="bg-white border border-gray-200 rounded-md p-4 flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-gray-900">New-Farmer Onboarding</div>
            <div className="text-[11px] text-gray-500">Full profile capture, documents, external refresh, and first-cycle scoring.</div>
          </div>
          <Button
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={() => onPick('NEW_FARMER')}
            data-testid="start-assessment-new-farmer-cta"
          >
            Start onboarding <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
const StartAssessmentFlow = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [farmers, setFarmers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1);
  const [farmer, setFarmer] = useState(null);
  const [previousAssessment, setPreviousAssessment] = useState(null);
  const [loadingPrev, setLoadingPrev] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/farmers`);
        setFarmers(res.data || []);
      } catch {
        toast.error('Failed to load customers');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const selectCustomer = async (f) => {
    setFarmer(f);
    setStep(2);
    setLoadingPrev(true);
    setPreviousAssessment(null);
    try {
      const res = await axios.get(`${API}/assessments/farmer/${f.id}`);
      const list = res.data?.assessments || [];
      const latest = list.find((a) => ['APPROVED', 'SCORED', 'REVIEW_PENDING'].includes(a.assessment_status)) || list[0] || null;
      setPreviousAssessment(latest);
    } catch {
      // ignore — just means we couldn't fetch prior
    } finally {
      setLoadingPrev(false);
    }
  };

  const addNew = () => navigate('/farmers/new?return_to=/assessment/new');

  const pickJourney = (jt) => {
    const params = new URLSearchParams();
    if (farmer?.id) params.set('farmer_id', farmer.id);
    if (previousAssessment?.id && jt !== 'NEW_FARMER') params.set('previous_assessment_id', previousAssessment.id);
    navigate(`/assessment/workflow/${jt}?${params.toString()}`);
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%)' }}>
      <nav className="bg-white border-b shadow-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <img src="/saakhsetu-logo.png" alt="SaakhSetu" style={{ height: '36px' }} />
              <span className="text-xs text-gray-400 font-normal hidden sm:inline" style={{ marginTop: '2px' }}>by Arbix AI Solutions</span>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')} data-testid="start-assessment-back-btn">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
          </div>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#1a202c' }} data-testid="start-assessment-title">
            Start New Assessment
          </h1>
          <p className="text-gray-500 text-sm">
            Pick the customer first, then choose the relationship type. This keeps the workflow aligned with how farmers walk into the branch.
          </p>
        </div>

        <Stepper step={step} />

        {step === 1 && (
          <CustomerStep farmers={farmers} loading={loading} onSelect={selectCustomer} onAddNew={addNew} />
        )}
        {step === 2 && farmer && (
          <RelationshipStep
            farmer={farmer}
            previousAssessment={previousAssessment}
            loadingPrev={loadingPrev}
            onPick={pickJourney}
            onBack={() => { setStep(1); setFarmer(null); setPreviousAssessment(null); }}
          />
        )}
      </main>
    </div>
  );
};

export default StartAssessmentFlow;
